import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {
  KNOWLEDGE_SOURCES,
  knowledgeLinkState,
  readKnowledgeSource,
} from 'analytics/knowledgeAnalytics';
import {Breadcrumb} from 'components/DSYS/Breadcrumb';
import {Layout} from 'components/DSYS/Layout';
import {MarkdownView} from 'components/DSYS/MarkdownView';
import {Title} from 'components/DSYS/Title';
import {getKnowledgeGuidePath, ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import {useLocation, useParams} from 'react-router';
import newsPostStyles from 'views/news/NewsPostRight.module.css';

import {useKnowledgeGuide} from './guideHooks';
import {KnowledgeContentLoading} from './KnowledgeView';
import styles from './KnowledgeView.module.css';
import {useHeadingAnchors} from './useHeadingAnchors';

const breadcrumbLinkState = knowledgeLinkState(KNOWLEDGE_SOURCES.breadcrumb);

// `updated_at` is a calendar date; format it in UTC so it doesn't shift a day.
const formatGuideDate = (value: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));

/** A curated in-repo guide (e.g. the internal glossary) rendered from Markdown. */
export const KnowledgeGuide = () => {
  const {guideId = ''} = useParams();
  const location = useLocation();
  const {data: guide, isLoading} = useKnowledgeGuide(guideId);
  const [content, setContent] = useState<HTMLDivElement | null>(null);

  useDocumentTitle(guide?.title ?? t('knowledge'));
  useHeadingAnchors(content, location.hash);

  useEffect(() => {
    analyticsLogger().logEvent('KNOWLEDGE_GUIDE_VIEWED', {
      guide_id: guideId,
      has_anchor: Boolean(location.hash),
      source: readKnowledgeSource(location.state),
    });
    // Log once per visit, with the source it was opened from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return (
    <Layout.Container>
      <div className={styles.knowledgeBody}>
        {isLoading ? (
          <KnowledgeContentLoading />
        ) : (
          <div className={newsPostStyles.newsPostRight}>
            <div
              className={styles.breadcrumbActionsRow}
              role="navigation"
              aria-label={t('knowledge_breadcrumb_nav')}
            >
              <Breadcrumb
                path={[
                  {to: ROUTE_PATHS.KNOWLEDGE, children: t('knowledge'), state: breadcrumbLinkState},
                  ...(guide
                    ? [
                        {
                          to: getKnowledgeGuidePath(guide.id),
                          children: guide.title,
                          state: breadcrumbLinkState,
                        },
                      ]
                    : []),
                ]}
                withMarginBottom={false}
              />
            </div>
            {guide ? (
              <>
                <div className={styles.pageTitleRow}>
                  <Title
                    className={`${newsPostStyles.title} ${styles.pageTitle}`}
                    weightVariant="emphasized"
                    size="large"
                  >
                    {guide.title}
                  </Title>
                </div>
                <Text color="subtle" size="small" className={styles.updatedDate}>
                  {t('knowledge_updated_date', {date: formatGuideDate(guide.updated_at)})}
                </Text>
                <Box paddingTop="16" ref={setContent}>
                  <MarkdownView value={guide.markdown} />
                </Box>
              </>
            ) : (
              <Box paddingY="24" role="status">
                <Text color="subtle">{t('knowledge_guide_not_found')}</Text>
              </Box>
            )}
          </div>
        )}
      </div>
    </Layout.Container>
  );
};
